import { useEffect, useState, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import styles from './EnterpriseSuperadmins.module.scss'

import { isApiError } from '@/api'
import {
  EnterpriseMember,
  EnterpriseRole,
  SetEnterpriseSuperadminRequestActionEnum,
  SetEnterpriseSuperadminResponse,
} from '@/api/generated'
import { getEnterpriseMembers, setEnterpriseSuperadmin } from '@/api/internal'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import { useToast } from '@/components/Toast/ToastContext'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import MemberSearch, { memberDisplayName } from '@/pages/app/Enterprises/components/CostCenterProvisioning/MemberSearch'
import { selectIsInternalSuperAdmin } from '@/store/features/user/userSlice'
import { getIsInternalUser } from '@/utils'

const SUPERADMIN_LIST_LIMIT = 100
const SEARCH_INPUT_ID = 'enterprise-superadmin-search'

interface EnterpriseSuperadminsProps {
  enterpriseId: string
  enterpriseName: string
}

interface PendingChange {
  member: EnterpriseMember
  action: SetEnterpriseSuperadminRequestActionEnum
}

const isSuperadmin = (member: EnterpriseMember) => member.role?.toLowerCase() === EnterpriseRole.Superadmin

export const grantBlockReason = (member: EnterpriseMember): string | null => {
  if (!member.email) return `${memberDisplayName(member)} has no email on file.`
  if (getIsInternalUser(member.email.toLowerCase())) return 'Wispr staff can only be made superadmin in the database.'
  if (isSuperadmin(member)) return `${member.email} is already a superadmin.`
  const role = member.role?.toLowerCase()
  if (role !== EnterpriseRole.Admin && role !== EnterpriseRole.Member) {
    return `Make ${member.email} an admin first, then make them superadmin.`
  }
  return null
}

export const describeSuperadminChange = (
  email: string,
  action: SetEnterpriseSuperadminRequestActionEnum,
  enterpriseName: string,
  { result, role, workos_synced: synced }: SetEnterpriseSuperadminResponse
): { message: string; type: 'success' | 'error' } => {
  const done =
    result === 'unchanged'
      ? `${email} was already ${role ?? 'unchanged'}`
      : action === 'grant'
        ? `Made ${email} superadmin`
        : `${email} is now an admin`
  if (synced) return { message: `${done}.`, type: 'success' }
  const retry = action === 'grant' ? 'make them superadmin again' : 'revoke again'
  return {
    message: `${done}, but their WorkOS role wasn't updated. Check that ${enterpriseName} has a WorkOS organization (set up through SSO or audit log streaming) and that ${email} is a member of it, then ${retry}.`,
    type: 'error',
  }
}

const EnterpriseSuperadmins = ({ enterpriseId, enterpriseName }: EnterpriseSuperadminsProps) => {
  const isInternalSuperAdmin = useSelector(selectIsInternalSuperAdmin)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const canManage = isInternalSuperAdmin && !impersonating
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [pending, setPending] = useState<PendingChange | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)

  useEffect(() => {
    setPending(null)
    setSearchError(null)
  }, [enterpriseId, canManage])

  const superadmins = useQuery({
    queryKey: ['enterprise-superadmins', enterpriseId],
    queryFn: async () => {
      const response = await getEnterpriseMembers(enterpriseId, {
        limit: SUPERADMIN_LIST_LIMIT,
        role: EnterpriseRole.Superadmin,
      })
      return response.data.members
    },
    enabled: canManage,
  })

  const change = useMutation({
    mutationFn: ({ member, action }: PendingChange) =>
      setEnterpriseSuperadmin(enterpriseId, member.email ?? '', action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-superadmins', enterpriseId] })
      queryClient.invalidateQueries({ queryKey: ['enterprise-members', enterpriseId] })
    },
  })

  if (!canManage) return null

  const handleSelect = (member: EnterpriseMember) => {
    const reason = grantBlockReason(member)
    setSearchError(reason)
    if (!reason) setPending({ member, action: 'grant' })
  }

  const handleConfirm = () => {
    if (!pending) return
    const email = pending.member.email ?? ''
    change.mutate(pending, {
      onSuccess: ({ data }) => {
        const { message, type } = describeSuperadminChange(email, pending.action, enterpriseName, data)
        updateToast(message, type)
        setPending(null)
      },
      onError: (err) => {
        updateToast((isApiError(err) && err.detail) || 'Failed to update superadmin', 'error')
      },
    })
  }

  const isGrant = pending?.action === 'grant'

  return (
    <div className={styles.section}>
      <p className={styles.sectionDescription}>
        Superadmins can manage {enterpriseName}&apos;s API keys. Their WorkOS role is updated to match.
      </p>

      {superadmins.isLoading && <p className={styles.muted}>Loading superadmins...</p>}
      {superadmins.isError && <p className={styles.fieldError}>Failed to load superadmins. Please try again.</p>}
      {superadmins.isSuccess && superadmins.data.length === 0 && <p className={styles.muted}>No superadmin yet.</p>}
      {superadmins.isSuccess && superadmins.data.length > 0 && (
        <ul className={styles.list} aria-label="Current superadmins">
          {superadmins.data.map((member) => (
            <li key={member.userId} className={styles.row}>
              <div className={styles.identity}>
                <span className={styles.name}>{memberDisplayName(member)}</span>
                {member.email && <span className={styles.email}>{member.email}</span>}
              </div>
              {member.email && !getIsInternalUser(member.email.toLowerCase()) && (
                <Button
                  title="Revoke"
                  type="destructiveLight"
                  ariaLabel={`Revoke superadmin: ${member.email}`}
                  onClick={() => {
                    setSearchError(null)
                    setPending({ member, action: 'revoke' })
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <MemberSearch
        enterpriseId={enterpriseId}
        disabled={change.isPending}
        onSelect={handleSelect}
        label="Add a superadmin"
        hint="Search this enterprise's members by name or email."
        inputId={SEARCH_INPUT_ID}
      />
      {searchError && <p className={styles.fieldError}>{searchError}</p>}

      <ConfirmDialog
        isOpen={pending !== null}
        title={isGrant ? 'Make superadmin' : 'Revoke superadmin'}
        message={
          isGrant
            ? `${pending?.member.email} will become a superadmin of ${enterpriseName} and can manage its API keys.`
            : `${pending?.member.email} will become an admin of ${enterpriseName} and lose API key management.`
        }
        confirmLabel={isGrant ? 'Make superadmin' : 'Revoke'}
        confirmType={isGrant ? 'dark' : 'destructive'}
        isConfirming={change.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
    </div>
  )
}

export default EnterpriseSuperadmins
